// @greenlight-spec: 28-it-dashboard-ui.md
import type { AppCrashDetail, AppCrashListItem } from '@greenlight/contract';

import { humanCrashReason } from '@/components/health/CrashesSection';

export function crashReasonLabel(item: Pick<AppCrashListItem, 'reason' | 'exit_code'>): string {
  return humanCrashReason(item.reason, item.exit_code);
}

const UNAVAILABLE_COPY: Readonly<Record<string, string>> = {
  no_container_id: 'The kubelet did not report which container ended.',
  read_failed: 'The previous container log could not be read.',
  container_gone: 'The pod had already replaced the container that crashed.',
};

export function crashLogUnavailableCopy(reason: string | null): string {
  return `No log was captured. ${UNAVAILABLE_COPY[reason ?? ''] ?? ''}`.trim();
}

/** The selected crashes as one document, oldest first, matching the other feedback downloads. */
export function renderAppCrashesMarkdown(items: readonly AppCrashDetail[]): string {
  const sections = [...items]
    .sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
    .map((item) =>
      [
        `## ${item.app_slug} · ${crashReasonLabel(item)}`,
        '',
        `- App: ${item.app_name} (\`${item.app_slug}\`)`,
        `- Reason: ${item.reason}${item.exit_code === null ? '' : ` (exit ${item.exit_code})`}`,
        `- Image: \`${item.image}\``,
        `- Occurred: ${item.occurred_at}`,
        ...(item.occurrence_count > 1
          ? [`- Occurrences: ${item.occurrence_count}, last at ${item.last_occurred_at}`]
          : []),
        '',
        '### Log',
        '',
        item.log_tail === null
          ? crashLogUnavailableCopy(item.log_unavailable_reason)
          : [
              ...(item.log_truncated ? ['_Older output was cut to fit the 256 KiB cap._', ''] : []),
              '```text',
              item.log_tail.replace(/```/g, '`​``'),
              '```',
            ].join('\n'),
      ].join('\n'),
    );
  return `# Greenlight app crashes\n\n${sections.join('\n\n---\n\n')}\n`;
}
